import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {Typography, FormControl, Select, MenuItem, FormHelperText, Alert, Link as MuiLink} from '@mui/material'
import {Controller, useWatch} from 'react-hook-form'
import {Link} from 'react-router-dom'
import {useAuth} from '../../../../contexts/AuthContext'

export default ({form, visitDetailsData}) => {
    const {permissions} = useAuth()
    // The chosen service care was dropped because the selected caregiver isn't assigned to it.
    const [serviceDropped, setServiceDropped] = useState(false)
    const chosenId = useWatch({control: form.control, name: 'service_agreement_item_id'})

    useEffect(() => {
        // The caregiver's services aren't known yet (all steps are mounted up front on mobile).
        if (!visitDetailsData) return
        const savedId = form.getValues('service_agreement_item_id')
        const serviceItem = visitDetailsData?.service_items?.find(item => item?.id === savedId)
        setServiceDropped(!serviceItem && !!savedId)
        if (serviceItem) {
            form.setValue('start_date', serviceItem?.start_date)
            form.setValue('end_date', serviceItem?.end_date)
            form.setValue('total_cost', serviceItem?.total_cost)
        } else {
            form.setValue('service_agreement_item_id', null)
        }
    }, [visitDetailsData])

    const serviceChange = serviceItemId => {
        setServiceDropped(false)
        const serviceItem = visitDetailsData?.service_items?.find(item => item?.id === serviceItemId)
        if (serviceItem) {
            form.setValue('start_date', serviceItem?.start_date)
            form.setValue('end_date', serviceItem?.end_date)
            form.setValue('total_cost', serviceItem?.total_cost)
        }
    }

    // The activities belong to the service agreement item and are only shown here: the backend copies them
    // onto the schedule and its visits, and they are edited on the client's service agreement.
    const serviceItem = visitDetailsData?.service_items?.find(item => item?.id === chosenId)
    const activities = [...(serviceItem?.activities ?? [])].sort((a, b) => (a?.display_order ?? 0) - (b?.display_order ?? 0))
    const agreementPath =
        visitDetailsData?.client_id && visitDetailsData?.service_agreement_id && permissions?.includes('clients.write')
            ? `/clients-management-nosublayout/${visitDetailsData.client_id}/service-agreement/service-agreement-form/${visitDetailsData.service_agreement_id}`
            : null

    return (
        <>
            <Typography variant="inputLabel" color="text.secondary">
                Service Care{' '}
                <Typography variant="inputLabel" color="error">
                    *
                </Typography>
            </Typography>
            <FormControl fullWidth error={!!form.formState.errors['service_agreement_item_id']}>
                <Controller
                    {...form.register('service_agreement_item_id')}
                    control={form.control}
                    render={({field}) => (
                        <Select
                            {...field}
                            // Only a service the caregiver offers can be shown as chosen.
                            value={visitDetailsData?.service_items?.some(item => item?.id === field.value) ? field.value : ''}
                            onChange={e => {
                                field.onChange(e.target.value)
                                serviceChange(e.target.value)
                            }}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                    },
                                },
                            }}
                            displayEmpty
                            renderValue={selected => {
                                const display_name = visitDetailsData?.service_items?.find(item => item?.id === selected)?.display_name
                                if (display_name) {
                                    return display_name
                                } else {
                                    return <span style={{color: '#aaa'}}>Select a service care</span>
                                }
                            }}>
                            {visitDetailsData?.service_items?.length > 0 ? (
                                visitDetailsData.service_items.map(item => (
                                    <MenuItem key={item?.id} value={item?.id}>
                                        {item?.display_name}
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem disabled>No service care is available for this caregiver</MenuItem>
                            )}
                        </Select>
                    )}
                />
                <FormHelperText>{form.formState.errors['service_agreement_item_id']?.message}</FormHelperText>
            </FormControl>
            {serviceDropped && (
                <Alert severity="warning" style={{marginTop: 8}}>
                    The service care you chose is not available for this caregiver. Choose another one.
                </Alert>
            )}
            {serviceItem && (
                <div className="activity-section">
                    <Typography variant="inputLabel" color="text.secondary">
                        Activities
                    </Typography>
                    <Typography variant="body2" color="text.secondary" className="activity-hint">
                        Read-only here. Edit these on the client’s{' '}
                        {agreementPath ? (
                            <MuiLink component={Link} to={agreementPath} target="_blank" rel="noopener noreferrer">
                                service agreement
                            </MuiLink>
                        ) : (
                            'service agreement'
                        )}
                        .
                    </Typography>
                    {activities.length > 0 ? (
                        <ol className="activity-container" aria-label="Activities">
                            {activities.map((activity, index) => (
                                <li key={activity?.id ?? index} className="activity-card">
                                    <span className="activity-order">{index + 1}</span>
                                    <div className="activity-text">
                                        <Typography variant="body1" color="text.primary">
                                            {activity?.activity_name}
                                        </Typography>
                                        {activity?.instructions && (
                                            <Typography variant="body2" color="text.secondary">
                                                {activity.instructions}
                                            </Typography>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ol>
                    ) : (
                        <Typography variant="body2" color="text.secondary" className="activity-empty">
                            This service care has no activities.
                        </Typography>
                    )}
                </div>
            )}
        </>
    )
}
